import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { useAuth } from "../contexts/CombinedAuthContext";
import { usePlans } from "../contexts/PlansContext";
import { functions, firestore, auth } from "../FirebaseConfig";
import { doc, onSnapshot as fsOnSnapshot } from "firebase/firestore";
import { useTokenPacks } from "../hooks/useTokenPacks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCoins,
  faShoppingCart,
  faCreditCard,
  faCheckCircle,
  faSpinner,
  faExclamationTriangle,
  faStore,
  faClock,
  faIdBadge,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/TokensAndProducts.css";

// Import UI components
import { SkeletonVariants } from "../components/ui/Skeleton";
import { EmptyState } from "../components/ui/EmptyState";
import { useToast } from "../components/ui/Toast";
import { CinematicHero, GlassSection, R5Tabs, R5Section, R5Card, R5Ribbon, R5BalanceBand } from "../components/ui";

// HIDDEN: legacy hard-coded TOKEN_PACKAGES — server now sources packs from
// `tokenPacks` collection via the `listVisibleTokenPacks` callable. The
// `art` field still drives the `TokenPackArt` SVG fallback when no
// `imagePath` / `imageVariants.thumb` is set on the pack document.
/*
const TOKEN_PACKAGES_LEGACY = [
  { id: "token-10", name: "10 Tokens", price: 9.99, quantity: 10, popular: false, art: "pouch" },
  { id: "token-25", name: "25 Tokens", price: 19.99, quantity: 25, popular: true, art: "chest" },
  { id: "token-50", name: "50 Tokens", price: 34.99, quantity: 50, popular: false, art: "hoard" },
  { id: "token-100", name: "100 Tokens", price: 59.99, quantity: 100, popular: false, art: "vault" },
];
*/

// ---------------------------------------------------------------------------
// Token-pack treasure illustrations (inline SVG)
// ---------------------------------------------------------------------------
// Hand-built vector scenes that escalate in treasure volume to mirror the
// pack tiers (pouch -> chest -> hoard -> vault). Themed via the brand CSS
// custom properties so a future palette tweak propagates automatically.
// Rendered inside R5Card's `image` slot (absolutely positioned over the
// gradient `imageBg` backdrop).
const PACK_ART_C = {
  navyDark: "var(--brand-navy-dark, #081523)",
  brass: "var(--brand-brass, #b8893f)",
  brassDark: "var(--brand-brass-dark, #8d6829)",
  brassLight: "var(--brand-brass-light, #d8b074)",
};

const PackCoin = ({ cx, cy, r = 9, rotate = 0 }) => (
  <g transform={`rotate(${rotate} ${cx} ${cy})`}>
    <circle cx={cx} cy={cy} r={r} fill={PACK_ART_C.brass} stroke={PACK_ART_C.brassDark} strokeWidth="1" />
    <circle cx={cx} cy={cy} r={r - 2.5} fill="none" stroke={PACK_ART_C.brassLight} strokeWidth="0.8" />
    <text
      x={cx}
      y={cy + r * 0.32}
      textAnchor="middle"
      fontSize={r * 0.95}
      fill={PACK_ART_C.brassDark}
      fontFamily="serif"
      fontWeight="700"
    >
      ★
    </text>
  </g>
);

const PackCoinStack = ({ x, y, count = 4, w = 28 }) => {
  const h = 4;
  return (
    <g>
      {Array.from({ length: count }).map((_, i) => {
        const cy = y - i * h;
        return (
          <g key={i}>
            <rect x={x} y={cy - h / 2} width={w} height={h} fill={PACK_ART_C.brass} stroke={PACK_ART_C.brassDark} strokeWidth="0.6" />
            <ellipse cx={x + w / 2} cy={cy - h / 2} rx={w / 2} ry={1.6} fill={PACK_ART_C.brassLight} stroke={PACK_ART_C.brassDark} strokeWidth="0.6" />
          </g>
        );
      })}
    </g>
  );
};

const PackChest = ({ x, y, w = 110, h = 60, openAngle = -28 }) => {
  const lidH = h * 0.45;
  return (
    <g>
      <ellipse cx={x + w / 2} cy={y + h + 6} rx={w / 2 + 6} ry={4} fill={PACK_ART_C.navyDark} opacity="0.45" />
      <rect x={x} y={y + lidH * 0.35} width={w} height={h - lidH * 0.35} rx="3" fill="#5a3a1a" stroke={PACK_ART_C.navyDark} strokeWidth="1.2" />
      <line x1={x + 4} y1={y + h - 14} x2={x + w - 4} y2={y + h - 14} stroke="#3e2710" strokeWidth="0.6" />
      <line x1={x + 4} y1={y + h - 4}  x2={x + w - 4} y2={y + h - 4}  stroke="#3e2710" strokeWidth="0.6" />
      <rect x={x} y={y + h - 22} width={w} height={4} fill={PACK_ART_C.brass} stroke={PACK_ART_C.brassDark} strokeWidth="0.6" />
      <rect x={x + w / 2 - 6} y={y + h - 26} width={12} height={12} fill={PACK_ART_C.brassLight} stroke={PACK_ART_C.brassDark} strokeWidth="0.8" />
      <circle cx={x + w / 2} cy={y + h - 20} r={1.6} fill={PACK_ART_C.navyDark} />
      <g transform={`rotate(${openAngle} ${x} ${y + lidH * 0.35})`}>
        <rect x={x} y={y - lidH + lidH * 0.35} width={w} height={lidH} rx="3" fill="#6b4520" stroke={PACK_ART_C.navyDark} strokeWidth="1.2" />
        <rect x={x} y={y + lidH * 0.35 - 4} width={w} height={3} fill={PACK_ART_C.brass} stroke={PACK_ART_C.brassDark} strokeWidth="0.5" />
      </g>
      <ellipse cx={x + w / 2} cy={y + lidH * 0.35 + 1} rx={w / 2 - 4} ry={3} fill={PACK_ART_C.brassLight} opacity="0.55" />
    </g>
  );
};

const PACK_ART_SCENES = {
  pouch: () => (
    <g transform="translate(110 60)">
      <ellipse cx="35" cy="78" rx="44" ry="6" fill={PACK_ART_C.navyDark} opacity="0.5" />
      <path
        d="M5 30 Q5 14 35 14 Q65 14 65 30 L70 70 Q70 84 35 84 Q0 84 0 70 Z"
        fill="#7a5a30"
        stroke={PACK_ART_C.navyDark}
        strokeWidth="1.2"
      />
      <path d="M10 40 L60 40 M10 52 L60 52 M10 64 L60 64" stroke="#5a3f20" strokeWidth="0.5" opacity="0.7" />
      <path d="M5 28 Q35 22 65 28" fill="none" stroke={PACK_ART_C.brassDark} strokeWidth="1.6" />
      <circle cx="5" cy="28" r="2" fill={PACK_ART_C.brassDark} />
      <circle cx="65" cy="28" r="2" fill={PACK_ART_C.brassDark} />
      <PackCoin cx={-18} cy={70} r={8} rotate={-12} />
      <PackCoin cx={-2} cy={78} r={7} rotate={20} />
      <PackCoin cx={82} cy={74} r={9} rotate={6} />
      <PackCoin cx={92} cy={66} r={6} rotate={-30} />
    </g>
  ),
  chest: () => (
    <g>
      <PackChest x={85} y={55} w={110} h={60} openAngle={-32} />
      <PackCoin cx={120} cy={92} r={7} rotate={-18} />
      <PackCoin cx={140} cy={96} r={9} rotate={4} />
      <PackCoin cx={160} cy={92} r={7} rotate={22} />
      <PackCoin cx={150} cy={86} r={6} rotate={-10} />
      <g fill={PACK_ART_C.brassLight} opacity="0.85">
        <path d="M70 35 l2 6 l6 2 l-6 2 l-2 6 l-2 -6 l-6 -2 l6 -2 z" />
        <path d="M210 28 l1.5 4 l4 1.5 l-4 1.5 l-1.5 4 l-1.5 -4 l-4 -1.5 l4 -1.5 z" />
        <circle cx="195" cy="48" r="1.6" />
      </g>
    </g>
  ),
  hoard: () => (
    <g>
      <PackCoinStack x={26} y={120} count={6} w={30} />
      <PackCoinStack x={222} y={120} count={5} w={30} />
      <PackChest x={88} y={50} w={108} h={62} openAngle={-36} />
      <PackCoin cx={108} cy={92} r={8} rotate={-22} />
      <PackCoin cx={128} cy={96} r={9} rotate={6} />
      <PackCoin cx={148} cy={94} r={9} rotate={-4} />
      <PackCoin cx={168} cy={98} r={8} rotate={18} />
      <PackCoin cx={184} cy={92} r={7} rotate={-10} />
      <PackCoin cx={94} cy={120} r={7} rotate={30} />
      <PackCoin cx={196} cy={122} r={7} rotate={-25} />
      <g fill={PACK_ART_C.brassLight} opacity="0.9">
        <path d="M62 24 l2 6 l6 2 l-6 2 l-2 6 l-2 -6 l-6 -2 l6 -2 z" />
        <path d="M218 18 l1.8 5 l5 1.8 l-5 1.8 l-1.8 5 l-1.8 -5 l-5 -1.8 l5 -1.8 z" />
      </g>
    </g>
  ),
  vault: () => (
    <g>
      <PackCoinStack x={14} y={118} count={7} w={28} />
      <PackCoinStack x={48} y={124} count={5} w={28} />
      <PackCoinStack x={208} y={118} count={7} w={28} />
      <PackCoinStack x={240} y={124} count={5} w={28} />
      <PackChest x={90} y={46} w={108} h={64} openAngle={-40} />
      <PackCoin cx={102} cy={88} r={8} rotate={-20} />
      <PackCoin cx={120} cy={94} r={10} rotate={4} />
      <PackCoin cx={140} cy={90} r={10} rotate={-10} />
      <PackCoin cx={160} cy={96} r={10} rotate={14} />
      <PackCoin cx={180} cy={90} r={8} rotate={-6} />
      <g transform="translate(140 78)">
        <path d="M-7 -2 L0 -10 L7 -2 L0 8 Z" fill="#a6332e" stroke={PACK_ART_C.navyDark} strokeWidth="0.8" />
        <path d="M-7 -2 L7 -2 L0 8 Z" fill="#7a2222" />
        <path d="M-7 -2 L0 -10 L0 -2 Z" fill="#c84a44" />
      </g>
      <PackCoin cx={88} cy={126} r={7} rotate={28} />
      <PackCoin cx={106} cy={130} r={7} rotate={-12} />
      <PackCoin cx={184} cy={130} r={7} rotate={20} />
      <PackCoin cx={202} cy={126} r={7} rotate={-26} />
      <g fill={PACK_ART_C.brassLight} opacity="0.95">
        <path d="M58 18 l2.5 7 l7 2.5 l-7 2.5 l-2.5 7 l-2.5 -7 l-7 -2.5 l7 -2.5 z" />
        <path d="M222 14 l2 6 l6 2 l-6 2 l-2 6 l-2 -6 l-6 -2 l6 -2 z" />
        <circle cx="200" cy="42" r="2" />
        <circle cx="76" cy="48" r="1.8" />
      </g>
    </g>
  ),
};

const PACK_ART_LABELS = {
  pouch: "Starter pouch with gold doubloons",
  chest: "Open treasure chest with gold coins",
  hoard: "Overflowing treasure chest with coin stacks",
  vault: "Captain's vault — chest, coin stacks, and a ruby",
};

function TokenPackArt({ variant = "pouch" }) {
  const Scene = PACK_ART_SCENES[variant] || PACK_ART_SCENES.pouch;
  const label = PACK_ART_LABELS[variant] || PACK_ART_LABELS.pouch;
  return (
    <svg
      className="token-pack-art"
      viewBox="0 0 280 160"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={label}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}
    >
      <title>{label}</title>
      <Scene />
    </svg>
  );
}

// HIDDEN: member store retired in token economy v2 — STORE_PRODUCTS,
// BUSINESS_TOKEN_SERVICES, renderStoreProducts, renderTokenManagement and
// the related tabs are kept here behind comment-blocks so that a future
// merch revival has a starting point. Do not re-enable without running it
// past plan v5. Chance-based perks (raffles, prize drawings) are off the
// table entirely — Doubloons are purchasable, so paid entry into a draw
// would make the Guild a gambling operator.
/*
const STORE_PRODUCTS = [
  { id: "prod-1", name: "Pirates Guild Mug", price: 14.99, image: "https://via.placeholder.com/150?text=Mug", description: "Ceramic mug with Pirates Guild logo - perfect for your morning rum... or coffee!", inStock: true },
  { id: "prod-2", name: "Island Adventure Map", price: 9.99, image: "https://via.placeholder.com/150?text=Map", description: "Waterproof map of the Virgin Islands with hidden coves and local spots marked.", inStock: true },
  { id: "prod-3", name: "Limited Edition Pirate Coin", price: 29.99, image: "https://via.placeholder.com/150?text=Coin", description: "Solid metal commemorative coin featuring the Pirates Guild emblem.", inStock: false },
  { id: "prod-4", name: "UV-Protected Lip Balm", price: 6.99, image: "https://via.placeholder.com/150?text=Lip+Balm", description: "Coconut-flavored lip balm with SPF 30 protection for sunny days at sea.", inStock: true },
  { id: "prod-5", name: "Reef-Safe Sunscreen", price: 12.99, image: "https://via.placeholder.com/150?text=Sunscreen", description: "Eco-friendly, reef-safe sunscreen that protects your skin and our coral reefs.", inStock: true },
];

const BUSINESS_TOKEN_SERVICES = [
  { id: "svc-1", name: "Happy Hour Discount", tokenCost: 2, description: "Offer a 20% discount during happy hour for token holders.", enabled: true },
  { id: "svc-2", name: "Free Appetizer", tokenCost: 3, description: "Complimentary appetizer with any meal purchase.", enabled: true },
  { id: "svc-4", name: "Premium Wifi Access", tokenCost: 1, description: "High-speed internet access for customers.", enabled: true },
  { id: "svc-5", name: "First-In-Line", tokenCost: 5, description: "Priority seating for token-paying customers.", enabled: true },
];
*/

function TokensAndProducts() {
  const { isAuthenticated } = useAuth();
  const { hasPaidSubscription } = usePlans();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { packs, loading: packsLoading, error: packsError } = useTokenPacks();

  const [activeTab, setActiveTab] = useState("purchase");
  // cartItems entries shape: { packId, name, quantity (tokens), unitPriceCents, units (qty bought) }
  const [cartItems, setCartItems] = useState([]);
  const [userTokens, setUserTokens] = useState(0);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // True if the current path is the /tokens/purchase/success landing.
  const onSuccessLanding = useMemo(
    () => /\/tokens\/purchase\/success(\/|$)/.test(location.pathname),
    [location.pathname],
  );

  const source = searchParams.get('src') || '';
  const intent = searchParams.get('intent') || '';

  const contextualMessage = useMemo(() => {
    if (source === 'wallet') {
      return {
        title: 'Your pass is ready. Keep tokens ready too.',
        body: 'Add tokens now so you can redeem immediately when staff scans your wallet pass.',
      };
    }
    if (source === 'account-history') {
      return {
        title: 'Stay ahead of your next redemption.',
        body: 'You are checking activity now. Top up Doubloons before your next outing if you want extra room for sponsored rewards.',
      };
    }
    if (source === 'header-balance') {
      return {
        title: 'Low balance check-in',
        body: 'A quick top-up keeps your Doubloon wallet ready for sponsored partner perks and event upgrades.',
      };
    }
    if (source === 'home-member') {
      if (intent === 'learn') {
        return {
          title: 'How Doubloons work',
          body: 'Doubloons are bonus loyalty rewards — not required for standard Guild deals. You earn them as you explore, and you can top up anytime if you want extra for special perks.',
        };
      }
      return {
        title: 'Top up your Doubloon balance',
        body: 'Add a Doubloon pack whenever you want extra room for sponsored partner rewards, event upgrades, and limited-time perks.',
      };
    }
    if (source === 'home-guest') {
      return {
        title: 'Doubloons, in plain English',
        body: 'Doubloons are the Guild loyalty rewards. They are not required to access standard Guild deals — they unlock bonus perks, sponsored partner rewards, event upgrades, and limited-time extras.',
      };
    }
    if (intent === 'compare') {
      return {
        title: 'Compare Doubloon packs before you buy',
        body: 'Choose the pack that matches how often you want bonus perks. Doubloons never expire in your wallet.',
      };
    }
    return {
      title: 'Doubloon packs & rewards',
      body: 'Top up your Doubloon balance for bonus perks. Standard Guild deals do not require Doubloons.',
    };
  }, [source, intent]);

  const valueCards = [
    {
      icon: faStore,
      title: 'Bonus rewards at participating businesses',
      body: 'Use Doubloons for sponsored partner rewards, event upgrades, and limited-time perks where available.',
    },
    {
      icon: faCreditCard,
      title: 'Faster in-venue checkout',
      body: 'Keep your pass and balance ready so staff can scan and confirm without delays.',
    },
    {
      icon: faClock,
      title: 'Top up before peak plans',
      body: 'Useful before nights out, travel days, and events where you expect to claim more bonus perks.',
    },
    {
      icon: faCoins,
      title: 'Flexible wallet balance',
      body: 'Tokens remain available in your wallet for future participating redemptions.',
    },
  ];

  // A PAID subscription gates token PURCHASES only. The free Deckhand floor
  // does not unlock Doubloon packs.
  const hasActiveSubscription = hasPaidSubscription();

  // ------------------------------------------------------------------
  // Fetch user's current token balance from Firestore (users/{uid}.tokenBalance)
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!isAuthenticated) { setUserTokens(0); return; }
    const fbUser = auth.currentUser;
    if (!fbUser) return;
    const userRef = doc(firestore, 'users', fbUser.uid);
    const unsub = fsOnSnapshot(userRef, (snap) => {
      const data = snap.data() || {};
      setUserTokens(typeof data.tokenBalance === 'number' ? data.tokenBalance : 0);
    });
    return () => unsub();
  }, [isAuthenticated]);

  // ------------------------------------------------------------------
  // Cart helpers — tied to server `tokenPacks` documents
  // ------------------------------------------------------------------
  const addPackToCart = (pack) => {
    setCartItems((prev) => {
      const idx = prev.findIndex((c) => c.packId === pack.id);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = { ...next[idx], units: Math.min(20, next[idx].units + 1) };
        return next;
      }
      return [
        ...prev,
        {
          packId: pack.id,
          name: pack.name,
          quantity: pack.quantity,
          unitPriceCents: pack.effectivePriceCents,
          units: 1,
        },
      ];
    });
    showToast(`${pack.name} added to yer cart!`, 'success');
  };

  const removeFromCart = (index) => {
    setCartItems((prev) => {
      const removed = prev[index];
      const next = prev.slice();
      next.splice(index, 1);
      if (removed) showToast(`${removed.name} removed from cart`, 'info');
      return next;
    });
  };

  const cartTotalCents = useMemo(
    () => cartItems.reduce((t, it) => t + it.unitPriceCents * it.units, 0),
    [cartItems],
  );
  const cartTotalDisplay = (cartTotalCents / 100).toFixed(2);

  // ------------------------------------------------------------------
  // Stripe Checkout launchers
  // ------------------------------------------------------------------
  const requireSubscriptionGuard = () => {
    if (hasActiveSubscription) return true;
    showToast(
      "An active Pirate's Guild subscription is needed before you can buy Doubloon packs.",
      'warning',
    );
    return false;
  };

  const handleBuyNow = async (pack) => {
    if (!isAuthenticated) { navigate('/login?redirect=/tokens'); return; }
    if (!requireSubscriptionGuard()) return;
    try {
      setCheckoutLoading(true);
      const fn = httpsCallable(functions, 'createCheckoutSession');
      const res = await fn({ packId: pack.id });
      const url = res.data && res.data.url;
      if (!url) throw new Error('Stripe did not return a checkout URL.');
      window.location.assign(url);
    } catch (err) {
      console.error('[Tokens] Buy Now failed', err);
      showToast(err.message || 'Could not start checkout. Try again.', 'error');
      setCheckoutLoading(false);
    }
  };

  const handleCartCheckout = async () => {
    if (!isAuthenticated) { navigate('/login?redirect=/tokens'); return; }
    if (cartItems.length === 0) return;
    if (!requireSubscriptionGuard()) return;
    try {
      setCheckoutLoading(true);
      const fn = httpsCallable(functions, 'createCheckoutSession');
      const res = await fn({
        cart: cartItems.map((c) => ({ packId: c.packId, quantity: c.units })),
      });
      const url = res.data && res.data.url;
      if (!url) throw new Error('Stripe did not return a checkout URL.');
      window.location.assign(url);
    } catch (err) {
      console.error('[Tokens] Cart checkout failed', err);
      showToast(err.message || 'Could not start checkout. Try again.', 'error');
      setCheckoutLoading(false);
    }
  };

  const handleOpenWallet = () => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/account/wallet');
      return;
    }
    navigate('/account/wallet');
  };

  // ------------------------------------------------------------------
  // Render: token packs grid (server-driven)
  // ------------------------------------------------------------------
  const renderTokenPackages = () => {
    if (packsError) {
      return (
        <R5Section title="Doubloon Packs" meta="Doubloons never expire">
          <EmptyState
            icon={faExclamationTriangle}
            title="Couldn't load packs"
            message={packsError}
            size="small"
          />
        </R5Section>
      );
    }
    if (!packsLoading && packs.length === 0) {
      return (
        <R5Section title="Doubloon Packs" meta="Doubloons never expire">
          <EmptyState
            icon={faStore}
            title="No packs available"
            message="Doubloon packs are not currently for sale. Check back soon!"
            size="small"
          />
        </R5Section>
      );
    }
    return (
      <R5Section title="Doubloon Packs" meta="Doubloons never expire">
        <div className="tokens-pack-guidance">
          Choose a pack based on how often you want bonus perks. Standard Guild deals do not require Doubloons — these packs unlock sponsored partner rewards and event upgrades.
        </div>
        {!hasActiveSubscription && (
          <div className="tokens-subscription-invite">
            🚢 Members earn bonus Doubloons faster and unlock premium Guild perks.{' '}
            <button
              type="button"
              className="tokens-subscription-gate__cta"
              onClick={() => navigate('/account/subscriptions')}
            >
              View membership plans →
            </button>
          </div>
        )}
        <div className="r5-grid--symmetric">
          {packs.map((pack) => {
            const priceDollars = (pack.effectivePriceCents / 100).toFixed(2);
            const [whole, cents] = priceDollars.split('.');
            const showStrike = pack.activeDiscount
              && typeof pack.priceCents === 'number'
              && pack.priceCents > pack.effectivePriceCents;
            const strike = showStrike ? (pack.priceCents / 100).toFixed(2) : null;
            const thumb = (pack.imageVariants && pack.imageVariants.thumb) || pack.imagePath || null;
            const ribbonText = pack.activeDiscount
              ? (pack.activeDiscount.label || 'Sale')
              : (pack.popular ? '★ Most Popular' : null);
            return (
              <R5Card
                key={pack.id}
                ribbon={ribbonText ? <R5Ribbon>{ribbonText}</R5Ribbon> : null}
                imageSrc={thumb || undefined}
                imageBg={thumb
                  ? undefined
                  : 'linear-gradient(135deg, var(--brand-navy), var(--brand-brass-dark))'}
                imageAlt={pack.name || ''}
                image={thumb ? null : <TokenPackArt variant={pack.art || 'pouch'} />}
              >
                <div className="r5-card__body r5-pack-body">
                  <div className="r5-card__category">
                    {pack.popular ? "Captain's Bundle" : 'Pack'}
                  </div>
                  <h3 className="r5-card__title">{pack.name}</h3>
                  {pack.description && (
                    <p className="r5-card__blurb">{pack.description}</p>
                  )}
                  <div className="r5-pack-price">
                    <span className="r5-pack-price__num">
                      {strike && (
                        <span className="r5-pack-price__strike">${strike}</span>
                      )}
                      <span className="r5-pack-price__currency">$</span>
                      {whole}
                      {cents && <span style={{ fontSize: 18 }}>.{cents}</span>}
                    </span>
                    <div className="r5-pack-cta-group">
                      <button
                        type="button"
                        className={`r5-pack-cta${pack.popular ? ' r5-pack-cta--bright' : ''}`}
                        onClick={() => handleBuyNow(pack)}
                        disabled={checkoutLoading}
                      >
                        {checkoutLoading ? (
                          <><FontAwesomeIcon icon={faSpinner} spin /> ...</>
                        ) : 'Top Up Now'}
                      </button>
                      <button
                        type="button"
                        className="r5-pack-cta r5-pack-cta--ghost"
                        onClick={() => addPackToCart(pack)}
                      >
                        + Cart
                      </button>
                    </div>
                  </div>
                </div>
              </R5Card>
            );
          })}
        </div>
      </R5Section>
    );
  };

  // HIDDEN: legacy renderStoreProducts retained inside the comment-block
  // wrapper above for future merch revivals; not rendered in v2.

  // Render shopping cart
  const renderShoppingCart = () => {
    if (cartItems.length === 0) {
      return (
        <div className="empty-cart">
          <EmptyState
            icon={faShoppingCart}
            title="Empty Cart"
            message="Yer cart is empty, matey! Add some tokens or products to get started."
            size="small"
          />
        </div>
      );
    }

    return (
      <div className="shopping-cart">
        <h3>Your Cart</h3>

        <div className="cart-items">
          {cartItems.map((item, index) => (
            <div key={index} className="cart-item">
              <div className="cart-item-info">
                <div className="cart-item-name">
                  <FontAwesomeIcon icon={faCoins} className="cart-item-icon" />
                  {item.name}
                  {item.units > 1 ? ` × ${item.units}` : ''}
                </div>
                <div className="cart-item-price">
                  ${((item.unitPriceCents * item.units) / 100).toFixed(2)}
                </div>
              </div>
              <button
                className="remove-item-btn"
                onClick={() => removeFromCart(index)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        <div className="cart-total">
          <div className="total-label">Total:</div>
          <div className="total-amount">${cartTotalDisplay}</div>
        </div>

        <button
          className="checkout-btn"
          onClick={handleCartCheckout}
          disabled={checkoutLoading}
        >
          {checkoutLoading ? (
            <><FontAwesomeIcon icon={faSpinner} spin /> Starting checkout…</>
          ) : (
            <><FontAwesomeIcon icon={faCreditCard} /> Proceed to Checkout</>
          )}
        </button>
      </div>
    );
  };

  // HIDDEN: business token-management UI retired in v2 — surfaced via the
  // dedicated /admin/token-packs and (upcoming) business StaffAdmin pages.

  // ------------------------------------------------------------------
  // Render: post-checkout success landing
  // ------------------------------------------------------------------
  if (onSuccessLanding) {
    return (
      <PurchaseSuccessView
        sessionId={searchParams.get('session_id')}
        onBack={() => navigate('/tokens', { replace: true })}
      />
    );
  }

  // ------------------------------------------------------------------
  // Main render
  // ------------------------------------------------------------------
  const tokensTabs = [
    { key: "purchase", label: "Buy Doubloons", icon: <FontAwesomeIcon icon={faCoins} /> },
  ];

  const balancePill = (
    <span className="hero-balance-pill">
      <FontAwesomeIcon icon={faCoins} />
      <strong>{userTokens}</strong> Doubloons
    </span>
  );

  return (
    <>
      <CinematicHero
        eyebrow="· Doubloons ·"
        title="Doubloon Packs & Rewards"
        dropCap="D"
        lede="Top up your Doubloon balance for bonus loyalty perks, sponsored partner rewards, event upgrades, and limited-time fun extras."
        bar={balancePill}
      />
      <GlassSection className="tokens-disclaimer-banner">
        <p>
          <strong>Heads up:</strong> You don't need Doubloons to use standard Guild deals.
          Doubloons are bonus loyalty rewards — you earn them as you explore the islands.
        </p>
      </GlassSection>
      <R5BalanceBand
        balance={userTokens}
        label={"Doubloons\naboard"}
        activity={[]}
        onLedgerClick={() => setActiveTab('purchase')}
        ledgerLabel="Buy More Doubloons →"
      />
      {tokensTabs.length > 1 && (
        <R5Tabs
          tabs={tokensTabs}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
      )}
      <GlassSection className="tokens-products-page">
      <div className="page-content">
        <section className="tokens-context-banner">
          <h2>{contextualMessage.title}</h2>
          <p>{contextualMessage.body}</p>
          <div className="tokens-context-actions">
            <button
              type="button"
              className="tokens-wallet-shortcut"
              onClick={handleOpenWallet}
            >
              <FontAwesomeIcon icon={faIdBadge} /> Open Wallet Pass
            </button>
          </div>
        </section>

        <section className="tokens-value-grid" aria-label="Why buy Doubloons">
          {valueCards.map((card) => (
            <article key={card.title} className="tokens-value-card">
              <div className="tokens-value-card__icon">
                <FontAwesomeIcon icon={card.icon} />
              </div>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </article>
          ))}
        </section>

        <section className="tokens-how-it-works" aria-label="How Doubloons work">
          <h3>How Doubloons work</h3>
          <ol>
            <li>Pick the Doubloon pack that matches your travel plans (or earn them as you explore).</li>
            <li>Complete checkout and watch Doubloons appear in your wallet.</li>
            <li>Show your wallet pass and redeem Doubloons for sponsored perks at participating businesses.</li>
          </ol>
        </section>

        {packsLoading ? (
          <div className="tokens-content-grid">
            <div className="main-content">
              <div className="token-packages">
                <SkeletonVariants.Token />
                <div className="token-package-grid">
                  {[1, 2, 3, 4].map((i) => (
                    <SkeletonVariants.Card key={i} />
                  ))}
                </div>
              </div>
            </div>
            <div className="side-content">
              <SkeletonVariants.Card />
            </div>
          </div>
        ) : (
          <div className="tokens-content-grid">
            <div className="main-content">
              {renderTokenPackages()}
            </div>
            <div className="side-content">{renderShoppingCart()}</div>
          </div>
        )}
      </div>
      </GlassSection>
    </>
  );
}

// ---------------------------------------------------------------------------
// PurchaseSuccessView
// ---------------------------------------------------------------------------
// Lands here after Stripe Checkout. Polls `tokenTransactions` filtered by the
// session_id query param until at least one row flips to status === 'completed'
// (the webhook is the source of truth). Shows a stalled-but-helpful state if
// the webhook hasn't caught up yet — refunds/edits are out of scope for v2.
function PurchaseSuccessView({ sessionId, onBack }) {
  const [status, setStatus] = useState('waiting'); // waiting | completed | failed | timeout
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!sessionId) { setStatus('failed'); return; }
    const fbUser = auth.currentUser;
    if (!fbUser) { setStatus('failed'); return; }
    const q = query(
      collection(firestore, 'tokenTransactions'),
      where('userId', '==', fbUser.uid),
      where('stripeSessionId', '==', sessionId),
      orderBy('createdAt', 'desc'),
      limit(20),
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRows(docs);
      if (docs.length === 0) return;
      const allDone = docs.every((d) => d.status === 'completed');
      const anyFailed = docs.some((d) => d.status === 'failed');
      if (anyFailed) setStatus('failed');
      else if (allDone) setStatus('completed');
    });
    const t = setTimeout(() => {
      setStatus((cur) => (cur === 'waiting' ? 'timeout' : cur));
    }, 30000);
    return () => { unsub(); clearTimeout(t); };
  }, [sessionId]);

  const totalTokens = rows.reduce((t, r) => t + (r.amount || 0), 0);

  return (
    <GlassSection className="tokens-products-page">
      <div className="purchase-success">
        {status === 'completed' && (
          <EmptyState
            icon={faCheckCircle}
            title="Doubloons credited!"
            message={`${totalTokens} Doubloon${totalTokens === 1 ? '' : 's'} have been added to your wallet. Set sail!`}
            size="medium"
            actionText="Back to Doubloons" onAction={onBack}
          />
        )}
        {status === 'waiting' && (
          <EmptyState
            icon={faSpinner}
            title="Confirming your purchase…"
            message="Stripe is letting us know your payment was successful. This usually takes just a few seconds."
            size="medium"
          />
        )}
        {status === 'timeout' && (
          <EmptyState
            icon={faExclamationTriangle}
            title="Still waiting on Stripe"
            message="Your payment likely succeeded but our confirmation hasn't arrived yet. Tokens will appear in your wallet shortly. If they don't, contact support with your receipt."
            size="medium"
            actionText="Back to tokens" onAction={onBack}
          />
        )}
        {status === 'failed' && (
          <EmptyState
            icon={faExclamationTriangle}
            title="We couldn't verify the purchase"
            message="If you completed payment, your tokens will still be credited automatically. Otherwise return to the tokens page and try again."
            size="medium"
            actionText="Back to tokens" onAction={onBack}
          />
        )}
      </div>
    </GlassSection>
  );
}

export default TokensAndProducts;
